import React, { useState } from 'react';
import { X } from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import { AZURE } from '../../services/api';
import { useSportsLang } from '../../contexts/SportsLangContext';

// ═══════════════════════════════════════════════════════════════════════════════
// AddAthleteModal — cadastro de atleta em 2 passos (dados → medidas iniciais)
//
// Extraído de RosterPage.jsx (Fase 1 — RosterPage vira só Staff).
// Hoje vive na CategoryDetailPage: quando `categoryId` é passado, o atleta
// nasce já atribuído àquela categoria (backend: POST /roster/athletes chama
// sp_assign_athlete_category quando category_id vem no body).
// ═══════════════════════════════════════════════════════════════════════════════

const LABELS = {
  pt: {
    addAthleteTitle: 'Cadastrar Atleta',
    initialMeasures: 'Medidas Iniciais',
    athleteBasicData: 'Dados basicos do atleta',
    measuresOptional: 'Opcional — pode preencher depois',
    errorCreatingAthlete: 'Erro ao criar atleta: ',
    labelFullName: 'Nome completo *',
    labelBirthDate: 'Data de nasc.',
    labelGender: 'Gênero',
    male: 'Masculino',
    female: 'Feminino',
    labelDocument: 'Documento (CPF/RG)',
    optional: 'Opcional',
    labelEmail: 'E-mail',
    inviteCheckbox: 'Enviar convite de acesso por e-mail',
    inviteHint: 'O atleta recebe um link para criar ou vincular sua conta na VUKZ AI.',
    inviteNeedsEmail: 'Informe o e-mail para enviar o convite.',
    resultTitle: '✓ Atleta cadastrado',
    resultEmailSent: 'Convite enviado para',
    resultEmailFailed: '⚠ Não foi possível enviar o e-mail de convite.',
    resultShareHint: 'Envie o link por outro canal abaixo.',
    btnCopyLink: 'Copiar link',
    btnCopied: '✓ Copiado',
    btnWhatsApp: 'WhatsApp',
    btnFinish: 'Concluir',
    waMessage: 'Você foi convidado(a) para a VUKZ AI. Acesse:',
    btnNextMeasures: 'Próximo — Medidas',
    labelHeight: 'Altura (cm)',
    labelWeight: 'Peso (kg)',
    labelWingspan: 'Envergadura (cm)',
    labelBodyFat: '% Gordura',
    btnSkip: 'Pular',
    btnSaveMeasures: 'Salvar Medidas',
    saving: 'Salvando...',
  },
  en: {
    addAthleteTitle: 'Add Athlete',
    initialMeasures: 'Initial Measurements',
    athleteBasicData: 'Basic athlete data',
    measuresOptional: 'Optional — can be filled in later',
    errorCreatingAthlete: 'Error creating athlete: ',
    labelFullName: 'Full name *',
    labelBirthDate: 'Date of birth',
    labelGender: 'Gender',
    male: 'Male',
    female: 'Female',
    labelDocument: 'Document (ID)',
    optional: 'Optional',
    labelEmail: 'Email',
    inviteCheckbox: 'Send access invitation by email',
    inviteHint: 'The athlete receives a link to create or link their VUKZ AI account.',
    inviteNeedsEmail: 'Enter the email to send the invitation.',
    resultTitle: '✓ Athlete registered',
    resultEmailSent: 'Invitation sent to',
    resultEmailFailed: '⚠ The invitation email could not be sent.',
    resultShareHint: 'Share the link via another channel below.',
    btnCopyLink: 'Copy link',
    btnCopied: '✓ Copied',
    btnWhatsApp: 'WhatsApp',
    btnFinish: 'Finish',
    waMessage: 'You have been invited to VUKZ AI. Access:',
    btnNextMeasures: 'Next — Measurements',
    labelHeight: 'Height (cm)',
    labelWeight: 'Weight (kg)',
    labelWingspan: 'Wingspan (cm)',
    labelBodyFat: 'Body Fat %',
    btnSkip: 'Skip',
    btnSaveMeasures: 'Save Measurements',
    saving: 'Saving...',
  },
};

const AddAthleteModal = ({ open, onClose, clubId, categoryId = null, onSuccess }) => {
  const [step, setStep] = useState(1); // 1=dados, 2=medidas, 3=resultado convite
  const [form, setForm] = useState({ full_name: '', birth_date: '', gender: 'male', document_id: '', email: '' });
  // INV-5A: convite criado no PASSO 1 junto com o atleta — medidas são
  // complementares e não determinam identidade/acesso. Token só em memória.
  const [sendInvite, setSendInvite] = useState(true);
  const [inviteResult, setInviteResult] = useState(null); // {email, sent, inviteUrl|null}
  const [copied, setCopied] = useState(false);
  const [measures, setMeasures] = useState({ height_cm: '', weight_kg: '', wingspan_cm: '', body_fat_pct: '' });
  const [submitting, setSubmitting] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  const inviteRequested = sendInvite && form.email.trim().length > 0;

  const resetForm = () => {
    setStep(1); setCreatedId(null);
    setForm({ full_name: '', birth_date: '', gender: 'male', document_id: '', email: '' });
    setMeasures({ height_cm: '', weight_kg: '', wingspan_cm: '', body_fat_pct: '' });
    setSendInvite(true); setInviteResult(null); setCopied(false);
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    if (!form.full_name) return;
    // Convite marcado sem e-mail → bloqueia (mensagem já visível no form)
    if (sendInvite && !form.email.trim()) return;
    setSubmitting(true);
    try {
      const body = { ...form, institution_id: clubId };
      if (!body.email.trim()) delete body.email;
      if (inviteRequested) body.send_invite = true;
      // Fail-closed: só envia category_id se numérico válido — mesmo pattern do
      // getAthletesByCategory (string viraria None no backend type=int).
      const catNum = Number(categoryId);
      if (Number.isFinite(catNum) && catNum > 0) body.category_id = catNum;
      const res = await AZURE.post('/roster/athletes', body);
      const aid = res.data?.data?.athlete_id;
      setCreatedId(aid);
      if (inviteRequested) {
        const token = res.data?.invite?.invite_token || null;
        setInviteResult({
          email: form.email.trim(),
          sent: res.data?.delivery?.email?.sent === true,
          inviteUrl: token
            ? `${window.location.origin}/accept-invite?token=${token}`
            : null,
        });
      }
      setStep(2);
    } catch (err) {
      console.error('Create athlete error:', err);
      alert(t.errorCreatingAthlete + (err?.response?.data?.error || err.message));
    } finally { setSubmitting(false); }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    const hasAny = Object.values(measures).some(v => v !== '' && v !== null);
    if (!hasAny) { finishMeasures(); return; }
    setSubmitting(true);
    try {
      const payload = { measurement_date: new Date().toISOString().split('T')[0] };
      if (measures.height_cm) payload.height_cm = parseFloat(measures.height_cm);
      if (measures.weight_kg) payload.weight_kg = parseFloat(measures.weight_kg);
      if (measures.wingspan_cm) payload.wingspan_cm = parseFloat(measures.wingspan_cm);
      if (measures.body_fat_pct) payload.body_fat_pct = parseFloat(measures.body_fat_pct);
      await AZURE.post(`/roster/physical/athlete/${createdId}`, payload);
    } catch (err) {
      // Falha de medidas NÃO desfaz atleta/convite — segue para o resultado.
      console.error('Add measurement error:', err);
    } finally { setSubmitting(false); finishMeasures(); }
  };

  // Pós-medidas: se houve convite, mostra resultado (passo 3); senão fecha.
  const finishMeasures = () => {
    if (inviteResult) { setStep(3); return; }
    finishAndClose();
  };

  const finishAndClose = () => { onSuccess?.(); onClose(); resetForm(); };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteResult.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard indisponível — sem crash */ }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-extrabold" style={{ color: VUKZ.text }}>
              {step === 1 ? t.addAthleteTitle : step === 2 ? t.initialMeasures : t.resultTitle}
            </h2>
            <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
              {step === 1 ? t.athleteBasicData : step === 2 ? t.measuresOptional : ''}
            </p>
          </div>
          <button onClick={() => { onClose(); resetForm(); }} className="p-1 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1} className="space-y-4">
            <div>
              <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelFullName}</label>
              <input type="text" required value={form.full_name}
                onChange={(e) => setForm(f => ({ ...f, full_name: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelBirthDate}</label>
                <input type="date" value={form.birth_date}
                  onChange={(e) => setForm(f => ({ ...f, birth_date: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelGender}</label>
                <select value={form.gender} onChange={(e) => setForm(f => ({ ...f, gender: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }}>
                  <option value="male">{t.male}</option>
                  <option value="female">{t.female}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelDocument}</label>
              <input type="text" value={form.document_id}
                onChange={(e) => setForm(f => ({ ...f, document_id: e.target.value }))}
                placeholder={t.optional}
                className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelEmail}</label>
              <input type="email" value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder={t.optional}
                className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
            </div>
            <div className="border rounded-xl p-3" style={{ borderColor: VUKZ.border }}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={sendInvite} className="mt-0.5"
                  onChange={(e) => setSendInvite(e.target.checked)} />
                <span>
                  <span className="block text-xs font-bold" style={{ color: VUKZ.text }}>
                    {t.inviteCheckbox}
                  </span>
                  <span className="block text-[10px] mt-0.5" style={{ color: VUKZ.textLight }}>
                    {t.inviteHint}
                  </span>
                </span>
              </label>
              {sendInvite && !form.email.trim() && (
                <p className="text-[10px] mt-2" style={{ color: '#b91c1c' }}>
                  {t.inviteNeedsEmail}
                </p>
              )}
            </div>
            <button type="submit" disabled={submitting || (sendInvite && !form.email.trim())}
              className="w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ background: VUKZ_GRADIENT }}>
              {submitting ? t.saving : t.btnNextMeasures}
            </button>
          </form>
        ) : step === 2 ? (
          <form onSubmit={handleStep2} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelHeight}</label>
                <input type="number" step="0.1" value={measures.height_cm}
                  onChange={(e) => setMeasures(m => ({ ...m, height_cm: e.target.value }))}
                  placeholder="175.0" className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelWeight}</label>
                <input type="number" step="0.1" value={measures.weight_kg}
                  onChange={(e) => setMeasures(m => ({ ...m, weight_kg: e.target.value }))}
                  placeholder="70.0" className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelWingspan}</label>
                <input type="number" step="0.1" value={measures.wingspan_cm}
                  onChange={(e) => setMeasures(m => ({ ...m, wingspan_cm: e.target.value }))}
                  placeholder="180.0" className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1" style={{ color: VUKZ.textLight }}>{t.labelBodyFat}</label>
                <input type="number" step="0.1" value={measures.body_fat_pct}
                  onChange={(e) => setMeasures(m => ({ ...m, body_fat_pct: e.target.value }))}
                  placeholder="12.5" className="w-full px-3 py-2.5 rounded-xl border text-sm" style={{ borderColor: VUKZ.border }} />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={finishMeasures}
                className="flex-1 py-3 rounded-xl text-sm font-bold transition-opacity"
                style={{ border: `1px solid ${VUKZ.border}`, color: VUKZ.textLight, background: '#fff' }}>
                {t.btnSkip}
              </button>
              <button type="submit" disabled={submitting}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: VUKZ_GRADIENT }}>
                {submitting ? t.saving : t.btnSaveMeasures}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            {inviteResult?.sent ? (
              <div className="border rounded-xl p-3 text-sm"
                style={{ borderColor: '#86efac', backgroundColor: '#f0fdf4', color: '#166534' }}>
                {t.resultEmailSent} <strong>{inviteResult.email}</strong>.
              </div>
            ) : (
              <div className="border rounded-xl p-3 text-sm"
                style={{ borderColor: '#fde047', backgroundColor: '#fefce8', color: '#854d0e' }}>
                {t.resultEmailFailed}{' '}
                {inviteResult?.inviteUrl ? t.resultShareHint : ''}
              </div>
            )}
            {inviteResult?.inviteUrl && (
              <div className="flex gap-2">
                <button type="button" onClick={copyLink}
                  className="flex-1 py-2.5 rounded-xl border text-sm font-bold"
                  style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
                  {copied ? t.btnCopied : t.btnCopyLink}
                </button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`${t.waMessage} ${inviteResult.inviteUrl}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-1 py-2.5 rounded-xl border text-sm font-bold text-center"
                  style={{ borderColor: '#86efac', color: '#166534' }}>
                  {t.btnWhatsApp}
                </a>
              </div>
            )}
            <button type="button" onClick={finishAndClose}
              className="w-full py-3 rounded-xl text-sm font-bold text-white transition-opacity hover:opacity-90"
              style={{ background: VUKZ_GRADIENT }}>
              {t.btnFinish}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAthleteModal;
